import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { toast } from 'sonner';
import {
  Settings, Save, Loader2, Shield, Bell, FileText, 
  ChevronDown, ChevronUp, Plus, Trash2, ToggleLeft
} from 'lucide-react';

export default function EventConfigPanel() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSection, setExpandedSection] = useState('families');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const res = await api.events.getEventConfig();
      setConfig(res.data);
    } catch (err) {
      toast.error('Failed to load event configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const { type, ...updateData } = config;
      await api.events.updateEventConfig(updateData);
      toast.success('Configuration saved successfully');
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleFamily = (family) => {
    const families = config.active_event_families || [];
    const updated = families.includes(family)
      ? families.filter(f => f !== family)
      : [...families, family];
    setConfig({ ...config, active_event_families: updated });
  };

  const toggleContract = (idx) => {
    const contracts = [...(config.contracts || [])];
    contracts[idx] = { ...contracts[idx], active: !contracts[idx].active };
    setConfig({ ...config, contracts });
  };

  const updateContract = (idx, field, value) => {
    const contracts = [...(config.contracts || [])];
    contracts[idx] = { ...contracts[idx], [field]: value };
    setConfig({ ...config, contracts });
  };

  const addContract = () => {
    const contracts = [...(config.contracts || [])];
    contracts.push({ id: `custom_${Date.now()}`, name: '', version: '', active: true });
    setConfig({ ...config, contracts });
  };

  const removeContract = (idx) => {
    const contracts = (config.contracts || []).filter((_, i) => i !== idx);
    setConfig({ ...config, contracts });
  };

  const updateReminder = (key, value) => {
    setConfig({
      ...config,
      reminder_rules: { ...config.reminder_rules, [key]: value }
    });
  };

  const toggleMandatory = (field) => {
    setConfig({
      ...config,
      mandatory_fields: {
        ...config.mandatory_fields,
        [field]: !config.mandatory_fields[field]
      }
    });
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? '' : section);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!config) return null;

  const families = config.active_event_families || [];
  const allFamilies = [
    'Weather / Natural Event', 'Subcontractor / Supplier Default', 'Utility / Services Disruption',
    'Labour / Industrial Action', 'Information / Design Delay', 'Instruction / Variation / Scope Change',
    'Access / Possession Delay', 'Authority / Regulatory Delay', 'Force Majeure / Exceptional Event',
    'Physical Conditions / Unforeseen', 'Client / Employer Risk', 'Procurement / Supply Chain Delay',
    'Health & Safety / Environmental', 'Financial / Payment Delay', 'Other'
  ];

  const reminderRules = config.reminder_rules || {};
  const mandatoryFields = config.mandatory_fields || {};
  const contracts = config.contracts || [];

  const mandatoryFieldLabels = {
    clause_reference: 'Clause Reference',
    entitlement_category: 'Entitlement Category',
    date_became_known: 'Date Became Known',
    ongoing_or_ceased: 'Ongoing / Ceased Status',
    notice_type: 'Notice Type',
    contract_version: 'Contract Version',
  };

  return (
    <div className="space-y-4" data-testid="event-config-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold uppercase tracking-tight">Event Configuration</h3>
          <p className="text-sm text-slate-500">Manage event families, contracts, reminders, and classification rules</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-event-config-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Active Event Families */}
      <Card>
        <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('families')}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <ToggleLeft className="h-4 w-4" /> Active Event Families ({families.length}/{allFamilies.length})
            </CardTitle>
            {expandedSection === 'families' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </CardHeader>
        {expandedSection === 'families' && (
          <CardContent>
            <p className="text-xs text-slate-500 mb-3">Toggle which event families are available for selection when creating or classifying events.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {allFamilies.map(family => {
                const isActive = families.includes(family);
                return (
                  <div
                    key={family}
                    data-testid={`family-toggle-${family.replace(/[^a-z]/gi, '-').toLowerCase()}`}
                    className={`flex items-center justify-between p-2.5 rounded border transition-colors cursor-pointer ${
                      isActive ? 'bg-slate-50 border-slate-300' : 'bg-slate-100/50 border-slate-200 opacity-60'
                    }`}
                    onClick={() => toggleFamily(family)}
                  >
                    <span className="text-xs font-medium text-slate-700 truncate pr-2">{family}</span>
                    <Switch checked={isActive} onCheckedChange={() => toggleFamily(family)} className="shrink-0" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Contracts */}
      <Card>
        <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('contracts')}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <FileText className="h-4 w-4" /> Contract Types ({contracts.filter(c => c.active).length} active)
            </CardTitle>
            {expandedSection === 'contracts' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </CardHeader>
        {expandedSection === 'contracts' && (
          <CardContent>
            <p className="text-xs text-slate-500 mb-3">Configure available contract types for project assignment and clause mapping.</p>
            <div className="space-y-2">
              {contracts.map((contract, idx) => (
                <div key={contract.id || idx} className={`flex items-center gap-3 p-3 rounded border ${contract.active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`}>
                  <Switch
                    checked={contract.active}
                    onCheckedChange={() => toggleContract(idx)}
                    data-testid={`contract-toggle-${idx}`}
                  />
                  <Input
                    value={contract.name}
                    onChange={(e) => updateContract(idx, 'name', e.target.value)}
                    placeholder="Contract Name"
                    className="w-40 text-sm h-8"
                    data-testid={`contract-name-${idx}`}
                  />
                  <Input
                    value={contract.version}
                    onChange={(e) => updateContract(idx, 'version', e.target.value)}
                    placeholder="Version"
                    className="w-32 text-sm h-8"
                    data-testid={`contract-version-${idx}`}
                  />
                  <Badge className={contract.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                    {contract.active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={() => removeContract(idx)} className="text-red-500 hover:text-red-700 h-8 w-8 p-0">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={addContract} className="mt-3 text-xs" data-testid="add-contract-btn">
              <Plus className="h-3 w-3 mr-1" /> Add Contract Type
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Reminder Rules */}
      <Card>
        <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('reminders')}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <Bell className="h-4 w-4" /> Reminder Rules
            </CardTitle>
            {expandedSection === 'reminders' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </CardHeader>
        {expandedSection === 'reminders' && (
          <CardContent>
            <p className="text-xs text-slate-500 mb-4">Configure how often and when the system sends reminders for pending actions.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">Unclassified Event Reminder (days)</Label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={reminderRules.unclassified_reminder_days || 3}
                  onChange={(e) => updateReminder('unclassified_reminder_days', parseInt(e.target.value) || 3)}
                  className="mt-1 h-9"
                  data-testid="reminder-unclassified-days"
                />
                <p className="text-[10px] text-slate-400 mt-1">Alert after this many days without classification</p>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Notice Deadline Warning (days)</Label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={reminderRules.notice_deadline_warning_days || 5}
                  onChange={(e) => updateReminder('notice_deadline_warning_days', parseInt(e.target.value) || 5)}
                  className="mt-1 h-9"
                  data-testid="reminder-notice-days"
                />
                <p className="text-[10px] text-slate-400 mt-1">Warn before notice submission deadline</p>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Claim Deadline Warning (days)</Label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={reminderRules.claim_deadline_warning_days || 10}
                  onChange={(e) => updateReminder('claim_deadline_warning_days', parseInt(e.target.value) || 10)}
                  className="mt-1 h-9"
                  data-testid="reminder-claim-days"
                />
                <p className="text-[10px] text-slate-400 mt-1">Warn before claim submission deadline</p>
              </div>
              <div className="flex items-center justify-between p-3 rounded border bg-slate-50">
                <div>
                  <Label className="text-xs font-medium text-slate-600">Weekly Update Reminder</Label>
                  <p className="text-[10px] text-slate-400">Send weekly summary of pending items</p>
                </div>
                <Switch
                  checked={reminderRules.weekly_update_reminder || false}
                  onCheckedChange={(v) => updateReminder('weekly_update_reminder', v)}
                  data-testid="reminder-weekly-toggle"
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Mandatory Classification Fields */}
      <Card>
        <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('mandatory')}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <Shield className="h-4 w-4" /> Mandatory Classification Fields
            </CardTitle>
            {expandedSection === 'mandatory' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </CardHeader>
        {expandedSection === 'mandatory' && (
          <CardContent>
            <p className="text-xs text-slate-500 mb-3">Select which fields must be filled in during contractual classification before an event is considered fully classified.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(mandatoryFieldLabels).map(([key, label]) => (
                <div
                  key={key}
                  data-testid={`mandatory-field-${key}`}
                  className={`flex items-center justify-between p-3 rounded border transition-colors ${
                    mandatoryFields[key] ? 'bg-amber-50/50 border-amber-200' : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div>
                    <span className="text-sm font-medium text-slate-700">{label}</span>
                    {mandatoryFields[key] && (
                      <Badge className="ml-2 bg-amber-100 text-amber-700 text-[10px]">Required</Badge>
                    )}
                  </div>
                  <Switch
                    checked={mandatoryFields[key] || false}
                    onCheckedChange={() => toggleMandatory(key)}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
